import { Text } from '@pixi/react'
import { Text as PixiText, TextStyle } from 'pixi.js'
import { useOrderInfoStore } from '../domain/orderInfo/orderInfoStore'
import { useEffect, useRef, useState } from 'react'
import { useFeatureToggleStore } from '../domain/featureToggle/store/featureToggelStore'
import { FeatureToggleType } from '../domain/featureToggle/enum/FeatureToggleType'

const fontStyle = new TextStyle({
  fontSize: 20,
  fill: 0xffffff,
  fontFamily: '"Noto Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontWeight: 'bold',
})
/**
 * V1 版本，讀取 `FeatureToggleType.displayRefNoWhenPlacedOrder`（V1 toggle store）。
 * 只有還在使用 Feature Toggle **V1** 的遊戲才應該使用這個元件——V2 遊戲的 V1 toggle store
 * 永遠不會被填值，用這個元件會導致 ref no 永遠不顯示，不管後端把 V2 的 `displayRefNo` 設成什麼。
 * V2 遊戲請改用 {@link RefNoDisplayPluginFeatureToggleV2}。
 */
export function RefNoDisplayPlugin({
  portraitResolution = { width: 1080, height: 1920 },
  landscapeResolution = { width: 1920, height: 1080 },
  isPortrait = true,
}: {
  portraitResolution: { width: number, height: number }
  landscapeResolution?: { width: number, height: number }
  isPortrait?: boolean
}) {
  const refNo = useOrderInfoStore(x => x.refNo)
  const isFeatureEnable = useFeatureToggleStore(x => !!x.toggleList.get(FeatureToggleType.displayRefNoWhenPlacedOrder))
  const [xPosition, setXPosition] = useState<number>(0)
  const textRef = useRef<PixiText>(null)
  const isShow = !!refNo && isFeatureEnable
  const timer = useRef<NodeJS.Timer>()
  const resolution = isPortrait ? portraitResolution : landscapeResolution
  const scaleRatio = isPortrait ? (portraitResolution.width / 1080) : (landscapeResolution.width / 1920)

  useEffect(() => {
    if (!isShow) return
    let currentStep: 'fade-out' | 'fade-in' = 'fade-out'
    const newRefNo = refNo
    timer.current = setInterval(() => {
      if (!textRef.current) return
      const lastRefNoText = textRef.current.text.split(`Recent Ref No: `)[1] || ''
      if (currentStep === 'fade-out') {
        const next = lastRefNoText.slice(0, -1)
        textRef.current.text = `Recent Ref No: ${next}`
        if (next.length === 0) currentStep = 'fade-in'
      }
      else {
        const next = newRefNo.slice(0, lastRefNoText.length + 1)
        textRef.current.text = `Recent Ref No: ${next}`
        if (next.length === newRefNo.length) clearInterval(timer.current)
      }
    }, 20)

    return () => clearInterval(timer.current)
  }, [isShow, refNo])

  return isShow && (
    <>
      <Text
        x={xPosition}
        y={resolution.height - 8}
        text="Recent Ref No: 000000000000"
        style={fontStyle}
        anchor={[0, 1]}
        alpha={0}
        scale={scaleRatio}
        ref={(el) => {
          el?.width && setXPosition(resolution.width / 2 - (el.width / 2))
        }}
      />
      <Text name="RecentRefNo" x={xPosition} y={resolution.height - 8} style={fontStyle} anchor={[0, 1]} scale={scaleRatio} ref={textRef} />
    </>
  )
}
